import { Box, Fade, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { authApi } from './auth-api';
import {
    getEmailParams,
    getOAuthError,
    getOAuthQueryFromSearch,
    getRedirectPath,
    redirectAfterAuth,
} from './auth-utils';
import AuthSplitLayout from './AuthSplitLayout';
import AuthEmailStep from './AuthEmailStep';
import AuthRegisterCredentials from './AuthRegisterCredentials';
import { useAnimatedHeight } from '@/hooks/use-animated-height';
import { betterAuthClient } from '@/libs/better-auth-client';

type Step = 'email' | 'credentials';

const FADE_DURATION = 180;

export default function AuthRegister() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    // Invite flow carries the invited email (and lock flag) in the query — pre-fill it and skip the
    // email step, landing straight on the credentials step since the email is already known.
    const emailParams = useMemo(() => getEmailParams(), []);
    const initialStep: Step = emailParams.email ? 'credentials' : 'email';
    const [step, setStep] = useState<Step>(initialStep);
    // visibleStep trails step — it only changes after the fade-out completes,
    // so only one panel is ever mounted at a time (no height inflation).
    const [visibleStep, setVisibleStep] = useState<Step>(initialStep);

    const [email, setEmail] = useState(emailParams.email ?? '');

    const redirectPath = useMemo(() => getRedirectPath(), []);
    const oauthQuery = useMemo(() => getOAuthQueryFromSearch(), []);

    type BetterAuthResult<TData> = {
        data?: TData | null;
        error?: {
            message?: string;
        } | null;
    };

    const continueOAuthIfNeeded = useCallback(async (): Promise<boolean> => {
        if (!oauthQuery) {
            return false;
        }

        const result: BetterAuthResult<{ url?: string }> = await betterAuthClient.oauth2.continue({
            postLogin: true,
            oauth_query: oauthQuery,
        });

        if (result.error) {
            enqueueSnackbar(
                typeof result.error.message === 'string' ? result.error.message : t('auth.oauth.error.default'),
                { variant: 'error' },
            );
            return false;
        }

        const redirectUrl = result.data?.url;
        if (redirectUrl) {
            window.location.replace(redirectUrl);
            return true;
        }

        return false;
    }, [oauthQuery, enqueueSnackbar, t]);

    // Animate height based on the currently rendered step
    const { containerRef, contentRef, heightStyle } = useAnimatedHeight(visibleStep, 160);

    useEffect(() => {
        authApi
            .getSession()
            .then(async (data) => {
                if (!data?.session) {
                    return;
                }

                const continued = await continueOAuthIfNeeded();
                if (!continued) {
                    redirectAfterAuth(redirectPath);
                }
            })
            .catch(() => undefined);
    }, [redirectPath, continueOAuthIfNeeded]);

    // Show a snackbar if better-auth redirected back with ?error=
    useEffect(() => {
        const oauthError = getOAuthError();
        if (!oauthError) return;
        const message = t(`auth.social.error.${oauthError.code}`, {
            defaultValue: t('auth.social.error.default'),
        });
        enqueueSnackbar(message, { variant: 'error' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEmailContinue = (submittedEmail: string) => {
        setEmail(submittedEmail);
        setStep('credentials');
    };

    const handleBack = () => {
        setStep('email');
    };

    const handleSuccess = () => {
        void continueOAuthIfNeeded().then((continued) => {
            if (!continued) {
                redirectAfterAuth(redirectPath);
            }
        });
    };

    // When step changes, wait for fade-out before swapping the rendered panel
    useEffect(() => {
        if (step === visibleStep) return;
        const timer = setTimeout(() => setVisibleStep(step), FADE_DURATION);
        return () => clearTimeout(timer);
    }, [step, visibleStep]);

    return (
        <AuthSplitLayout
            footer={
                <Typography variant="body2" color="text.secondary">
                    {t('auth.register.have-account')}{' '}
                    <Typography
                        component={Link}
                        to="/auth/login"
                        color="primary.main"
                        sx={{ textDecoration: 'none', fontWeight: 600 }}
                    >
                        {t('auth.register.go-login')}
                    </Typography>
                </Typography>
            }
        >
            {/* Persistent header — always visible across both steps */}
            <Box mb={3}>
                <Typography variant="h5" fontWeight={700} mb={0.5}>
                    <Trans
                        i18nKey="auth.register.heading"
                        components={{
                            em: (
                                <Box
                                    component="em"
                                    sx={{
                                        color: 'primary.main',
                                        fontStyle: 'italic',
                                        fontWeight: 'inherit',
                                    }}
                                />
                            ),
                        }}
                    />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('auth.register.subheading')}
                </Typography>
            </Box>

            <Box ref={containerRef} sx={heightStyle}>
                <Fade in={step === visibleStep} timeout={FADE_DURATION}>
                    <Box ref={contentRef}>
                        {visibleStep === 'email' ? (
                            <AuthEmailStep
                                variant="register"
                                initialEmail={email}
                                redirectPath={redirectPath}
                                oauthQuery={oauthQuery}
                                onContinue={handleEmailContinue}
                                lockEmail={emailParams.lockEmail}
                            />
                        ) : (
                            <AuthRegisterCredentials
                                email={email}
                                redirectPath={redirectPath}
                                oauthQuery={oauthQuery}
                                onBack={handleBack}
                                onSuccess={handleSuccess}
                                lockEmail={emailParams.lockEmail}
                            />
                        )}
                    </Box>
                </Fade>
            </Box>
        </AuthSplitLayout>
    );
}
